import { put, call, delay, all, takeEvery, takeLatest } from 'redux-saga/effects'
import { putWait } from 'redux-saga-callback'
import { store as reduxStore } from 'index'
import { push } from 'react-router-redux'
import {
  list,
  get,
  add,
  del,
  update,
  settingsGet,
  settingsUpdate,
  settingsThreadAndConversationGet,
  settingsThreadAndConversationMap,
  updateSettings,
} from 'services/apps'
import { changeExtensionStatus as extensionChangeStatus } from 'services/extensions'
import { currentAccount, manualOutreach, updateProfile } from 'services/user'
import _ from 'lodash'
import Cookies from 'js-cookie'
import {
  isAuthErrorCode,
  isAppOwner,
  isAppAdmin,
  isAppModerator,
  isAppDeveloper,
  checkIfFreeEmailDomain,
  canListInvoices,
  getAppHost,
} from 'helpers'
import constants from '../../constants'
import actions from './actions'
import messagesActions from '../messages/actions'

export function* LIST({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        appLoadingInProgress: true,
      },
    })
    let current = 1

    const { appsListPageNo: currentPageState } = reduxStore.getState().apps
    current = currentPageState
    if (payload.currentPage) current = payload.currentPage
    const searchText = reduxStore.getState().apps.appSearchText
    const roleFilterList = payload.appRolesFilter ? payload.appRolesFilter : ''
    const planFilter = reduxStore.getState().apps.appPlanFilter
    const success = yield call(list, current, searchText, roleFilterList, planFilter)
    if (success && success.data) {
      let totalAppCount = success.data.length
      if (success.meta && success.meta.pagination.total && success.meta.pagination) {
        totalAppCount = success.meta.pagination.total
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          data: success.data,
          appsListTotal: totalAppCount,
          appsListPageNo: current,
          active: payload.firstAppData ? payload.firstAppData : [],
          appLoadingInProgress: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          data: [],
          appsListTotal: 0,
          active: [],
          appsListPageNo: 1,
          appLoadingInProgress: false,
        },
      })
      if (
        success.error !== undefined &&
        success.error.code !== undefined &&
        isAuthErrorCode(success.error.code)
      ) {
        yield put({
          type: 'user/LOGOUT',
        })
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        appLoadingInProgress: false,
      },
    })
  }
}

export function* ENTER_APP_TEXT({ payload }) {
  try {
    const { searchText, clearText } = payload
    if (clearText) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          appSearchText: '',
          appsListPageNo: 1,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          appSearchText: searchText,
          appsListPageNo: 1,
          loading: true,
        },
      })

      if (searchText.length === 0 || searchText.length >= 2) {
        yield put({
          type: actions.SEARCH_APP,
          payload: {},
        })
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* SEARCH_APP() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })

    const searchText = reduxStore.getState().apps.appSearchText

    const success = yield call(list, 1, searchText)

    if (success && success.data) {
      let totalAppCount = success.data.length
      if (success.meta && success.meta.pagination && success.meta.pagination.total) {
        totalAppCount = success.meta.pagination.total
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          data: success.data,
          appsListTotal: totalAppCount,
          active: [],
          loading: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          data: [],
          appsListTotal: 0,
          active: [],
          loading: false,
        },
      })
      if (
        success.error !== undefined &&
        success.error.code !== undefined &&
        isAuthErrorCode(success.error.code)
      ) {
        if (success.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
          yield put({
            type: 'user/LOGOUT',
          })
        } else {
          yield put(push('/login'))
        }
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* REMOVE_ACTIVE_APP() {
  // By design, this resets ONLY appCore's own slice (active/stats/loading).
  // The legacy apps/REMOVE_ACTIVE_APP additionally cleared ~24 props that now
  // live in the messages/misc/apiKeys/calls/teams slices. Rather than reach
  // across slices from here, each domain owns its own app-switch reset: when a
  // slice's page is migrated, that slice's root saga adds
  // `takeEvery(appCoreActions.REMOVE_ACTIVE_APP, RESET_ON_APP_SWITCH)`.
  // This is not a live regression today — every dispatch site still uses the
  // legacy apps/REMOVE_ACTIVE_APP. See .kiro/specs/pr-2431-followups/tracking.md
  // (CRITICAL #3) for the per-slice reset prop map.
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        active: [],
        stats: [],
        loading: false,
      },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* ADD({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })

    let region = _.trim(payload.region)
    let version = _.trim(payload.version)
    const { metadata } = payload

    let success = []
    if (payload.region === 'hipaa-us') {
      const post = {
        intent: constants.ManualOutreachIntents['app-create-request-us-hipaa-compliant'],
      }

      success = yield call(manualOutreach, post)
      if (success && success.data) {
        yield put({
          type: 'settings/SET_STATE',
          payload: {
            visibleModalID: 'appHipaaUSCreated',
          },
        })
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
        yield put(push('/apps'))
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
        if (
          success.error !== undefined &&
          success.error.code !== undefined &&
          isAuthErrorCode(success.error.code)
        ) {
          if (success.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
            yield put({
              type: 'user/LOGOUT',
            })
          } else {
            yield put(push('/login'))
          }
        } else if (success.error.code === 'ERR_APP_CREATION_FAILED_MAX_LIMIT') {
          yield put({
            type: 'settings/SET_STATE',
            payload: {
              visibleModalID: 'appCreationFailed',
            },
          })
        } else if (success.error && success.error.code === 'ERR_BAD_REQUEST') {
          window.triggerNotification(success.error.details.name, 'error')
        } else if (success.error.code !== undefined) {
          yield put({
            type: 'settings/SET_STATE',
            payload: {
              visibleModalID: 'appCreationFailedException',
            },
          })
        }
      }
    } else {
      if (region !== 'us' && region !== 'eu' && region !== 'in') region = 'us'
      if (version === undefined) version = '3'

      const post = {
        name: payload.name,
        region,
        version,
        metadata,
      }

      success = yield call(add, post)

      if (success && success.data) {
        const loggedInUserInfo = reduxStore.getState().user
        if (
          loggedInUserInfo.id &&
          loggedInUserInfo.createdAt &&
          loggedInUserInfo.createdAt < constants.featureDeprecatedTimestamps.legacy_widgets
        ) {
          yield call(extensionChangeStatus, 'widget', success.data, 'all')
        }

        if (Cookies.get('_ga') || Cookies.get('utm')) {
          let userGaId = ''
          if (Cookies.get('_ga')) {
            userGaId = Cookies.get('_ga')
          }
          let userUtm = {}
          if (Cookies.get('utm')) {
            userUtm = JSON.parse(Cookies.get('utm'))
          }
          let userUtmLast = {}
          if (Cookies.get('utm_last')) {
            userUtmLast = JSON.parse(Cookies.get('utm_last'))
          }
          const gaValuesForUsermeta = {
            meta: { ga_id: userGaId, utm: userUtm, utm_last: userUtmLast },
          }
          const userUpdateMeta = yield call(updateProfile, gaValuesForUsermeta)
          if (userUpdateMeta && userUpdateMeta.data !== undefined) {
            yield put({
              type: 'user/LOAD_CURRENT_ACCOUNT',
              data: userUpdateMeta.data,
            })
          }
        }

        yield put({
          type: actions.ENTER_APP_TEXT,
          payload: {
            searchText: '',
            clearText: true,
          },
        })
        yield put({
          type: actions.LIST,
          payload: { currentPage: 1 },
        })
        yield put({
          type: actions.SET_STATE,
          payload: {
            active: success.data,
            appCreated: success.data,
            loading: false,
          },
        })

        yield put({
          type: 'settings/SET_STATE',
          payload: {
            visibleModalID: 'congratulations',
          },
        })
        yield put(push(`/app/${success.data.id}/overview`))
        localStorage.setItem('cc_lastOpenedApp', success.data.id)
      } else {
        yield put({
          type: 'settings/SET_STATE',
          payload: {
            visibleModalID: 'appCreationFailed',
          },
        })
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
        if (
          success.error !== undefined &&
          success.error.code !== undefined &&
          isAuthErrorCode(success.error.code)
        ) {
          yield put(push('/login'))
        } else if (success.error.code === 'ERR_APP_CREATION_FAILED_MAX_LIMIT') {
          yield put({
            type: 'settings/SET_STATE',
            payload: {
              visibleModalID: 'appCreationFailed',
            },
          })
        } else if (success.error && success.error.code === 'ERR_BAD_REQUEST') {
          window.triggerNotification(success.error.details.name, 'error')
        } else if (success.error.code !== undefined) {
          yield put({
            type: 'settings/SET_STATE',
            payload: {
              visibleModalID: 'appCreationFailedException',
            },
          })
        }
        yield put(push('/apps'))
      }
    }
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
    return success
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* ADD_APP_ON_ONBOARDING({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })

    let region = _.trim(payload.region)
    let version = _.trim(payload.version)
    const { metadata } = payload

    let success = []

    if (region !== 'us' && region !== 'eu' && region !== 'in') region = 'us'
    if (version === undefined) version = '3'

    const post = {
      name: payload.name,
      region,
      version,
      metadata,
    }
    yield delay(2000)
    const loggedInUserInfo = reduxStore.getState().user

    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
        firstAppCreationInProgress: true,
      },
    })

    if (
      checkIfFreeEmailDomain(loggedInUserInfo.email) ||
      localStorage.getItem('ccPreviewBuilderId')
    ) {
      yield put(push('/apps'))
    } else {
      yield put({
        type: 'user/UPDATE_CUSTOMER_ON_DISPLAYING_KICKOFF_CALL_SCREEN',
      })
      yield put(push('/schedule-your-kick-off-call'))
    }

    success = yield call(add, post)

    if (success && success.data) {
      if (
        loggedInUserInfo.id &&
        loggedInUserInfo.createdAt &&
        loggedInUserInfo.createdAt < constants.featureDeprecatedTimestamps.legacy_widgets
      ) {
        yield call(extensionChangeStatus, 'widget', success.data, 'all')
      }

      yield put({
        type: actions.ENTER_APP_TEXT,
        payload: {
          searchText: '',
          clearText: true,
        },
      })

      yield put({
        type: actions.SET_STATE,
        payload: {
          active: success.data,
          appCreated: success.data,
          firstAppID: success.data.id,
        },
      })

      yield put({
        type: actions.LIST,
        payload: { currentPage: 1, firstAppData: success.data },
      })
      yield put({
        type: 'settings/SET_STATE',
        payload: {
          visibleModalID: 'congratulations',
        },
      })
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
          firstAppCreationInProgress: false,
        },
      })

      localStorage.setItem('cc_lastOpenedApp', success.data.id)
      yield put({
        type: 'settings/SET_STATE',
        payload: {
          showModalConfetti: true,
        },
      })
    } else {
      yield put({
        type: 'settings/SET_STATE',
        payload: {
          visibleModalID: 'appCreationFailed',
        },
      })
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
          firstAppCreationInProgress: false,
        },
      })
      if (
        success.error !== undefined &&
        success.error.code !== undefined &&
        isAuthErrorCode(success.error.code)
      ) {
        yield put(push('/login'))
      } else if (success.error.code === 'ERR_APP_CREATION_FAILED_MAX_LIMIT') {
        yield put({
          type: 'settings/SET_STATE',
          payload: {
            visibleModalID: 'appCreationFailed',
          },
        })
      } else if (success.error && success.error.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(success.error.details.name, 'error')
      } else if (success.error.code !== undefined) {
        yield put({
          type: 'settings/SET_STATE',
          payload: {
            visibleModalID: 'appCreationFailedException',
          },
        })
      }
    }

    return success
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
        firstAppCreationInProgress: false,
      },
    })
  }
}

export function* UPDATE_ACTIVE_APP({
  payload: {
    id = 0,
    otherAction = '',
    keyID = '',
    callSource = '',
    fetchAllData = true,
    callSourceRedirectURL = '',
    keyID2 = '',
  },
}) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        appLoadingInProgress: true,
      },
    })
    const { openLastVisitedApp } = reduxStore.getState().user

    const loggedInUserInfo = reduxStore.getState().user
    const currentAccountResponse = yield call(currentAccount, loggedInUserInfo.id)
    if (!(currentAccountResponse && currentAccountResponse.data)) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          appLoadingInProgress: false,
        },
      })
      yield put(push('/login'))
      return false
    }
    let allCallsProcessed = false
    const appPagePaths = [
      '/app/:appID/quick-start',
      '/app/:appID/credentials',
      '/app/:appID/users',
      '/app/:appID/roles',
      '/app/:appID/groups',
      '/app/:appID/groups/:keyID/edit',
      '/app/:appID/agents',
      '/app/:appID/webhooks-legacy',
      '/app/:appID/webhooks',
      '/app/:appID/extensions',
      '/app/:appID/general',
      '/app/:appID/analytics',
      '/app/:appID/ai',
      '/app/:appID/ai-bots',
      '/app/:appID/ai-features',
      '/app/:appID/ai-instructions',
      '/app/:appID/plans-billing',
      '/app/:appID/overview',
      '/app/:appID/messages',
      '/app/:appID/chats',
      '/app/:appID/chat-features',
      '/app/:appID/chat-settings',
      '/app/:appID/widgets',
      '/app/:appID/in-flight-message-moderation',
      '/app/:appID/group-moderation',
      '/app/:appID/moderation',
      '/app/:appID/moderation/legacy',
      '/app/:appID/members',
      '/app/:appID/calls',
      '/app/:appID/calls-legacy',
      '/app/:appID/builder/:builderId',
      '/app/:appID/integrate',
      '/app/:appID/ai-agents',
      '/app/:appID/integrate/apis',
      '/app/:appID/notifications',
      '/app/:appID/notifications-legacy',
      '/app/:appID/integrate/sdks',
      '/app/:appID/integrate/ui-kits',
      '/app/:appID/plans-billing',
      '/app/:appID/notifications-logs',
      '/app/:appID/calling-get-started',
      '/app/:appID/ai-agent/get-started',
      '/app/:appID/byo-agent/get-started',
      '/app/:appID/third-party-ai-agents',
      '/app/:appID/third-party-ai-agents/add',
      '/app/:appID/ai-actions',
      '/app/:appID/ai-actions/add',
      '/app/:appID/ai-tools',
      '/app/:appID/ai-tools/add',
      '/app/:appID/messaging-get-started',
      '/app/:appID/ai-agents/:agentUid/tool',
      '/app/:appID/ai-agents/:agentUid/tool/custom-api-tool',
      '/app/:appID/custom-tabs',
    ]
    const checkIfAppPath = _.indexOf(
      appPagePaths,
      reduxStore.getState().menu.globalRouteMatch.path,
    )
    if (id === 0) {
      if (checkIfAppPath >= 0) {
        id = reduxStore.getState().menu.globalRouteMatch.params.appID
      }
    }

    if (id !== 0) {
      let appData = { data: [] }
      let appSettings = { data: [] }
      appData = yield call(get, id)

      if (appData && appData.data) {
        if (appData.data.scheduledToDeleteAt && appData.data.state === 'inactive') {
          yield put({
            type: 'settings/SET_STATE',
            payload: {
              visibleModalID: 'sheduledTodelete',
            },
          })
        } else if (appData.data.scheduledToDeleteAt === undefined) {
          localStorage.setItem('cc_lastOpenedApp', appData.data.id)
          appSettings = yield call(settingsGet, appData.data.id, appData.data)

          if (isAppOwner(appData.data) || isAppAdmin(appData.data))
            if (callSource === 'appCard' && !isAppModerator(appData.data)) {
              yield put({
                type: actions.SET_STATE,
                payload: {
                  active: appData.data,
                },
              })
              yield put({
                type: 'apps/GET_METRICS',
                payload: { data: appData.data, appSettings: appSettings.data },
              })
              yield put({
                type: 'apps/API_KEYS_LIST',
                payload: { currentPage: 1, data: appData.data, appSettings: appSettings.data },
              })
              yield put({
                type: 'apps/GET_USAGE_AND_OVERAGES',
                payload: { data: appData.data, appSettings: appSettings.data },
              })
              yield put({
                type: 'apps/EXTENSIONS_LIST',
                data: appData.data,
              })
            }

          if (callSource === 'appCard') {
            yield put({
              type: 'apps/GET_MESSAGE_SEARCH_LOGS',
              data: appData.data,
            })
            if (isAppOwner(appData.data)) {
              yield put({
                type: 'plans/GET_PLANS',
              })
            }
          }
          if (fetchAllData && checkIfAppPath >= 0) {
            yield put({
              type: actions.SET_STATE,
              payload: {
                active: appData.data,
                appSettings: appSettings.data,
              },
            })
            yield put({
              type: 'apps/TEAM_COLLABORATOR_LIST',
              payload: { currentPage: 1, data: appData.data },
            })

            if (!isAppModerator(appData.data)) {
              yield put({
                type: 'apps/API_KEYS_LIST',
                payload: { currentPage: 1, data: appData.data, appSettings: appSettings.data },
              })
            }

            yield put({
              type: 'apps/GET_METRICS',
              payload: { data: appData.data, appSettings: appSettings.data },
            })
            yield put({
              type: actions.GET_APP_SETTINGS,
              data: appData.data,
            })
            yield put({
              type: actions.GET_APP_MESSAGE_SETTINGS,
              data: appData.data,
            })
            yield put({
              type: 'apps/GET_USAGE_AND_OVERAGES',
              payload: { data: appData.data, appSettings: appSettings.data },
            })

            if (
              callSource !== 'appCard' &&
              otherAction !== 'GET_MESSAGE_LOGS' &&
              otherAction !== 'GET_MOD_MESSAGE_LOGS' &&
              otherAction !== 'CHAT_WIDGETS_LIST'
            ) {
              yield put({
                type: 'apps/USERS_LIST',
                payload: {
                  currentPage: 1,
                  data: appData.data,
                  appSettings: appSettings.data,
                },
              })

              yield put({
                type: 'apps/AI_AGENT_USER_LIST',
                payload: {
                  currentPage: 1,
                  data: appData.data,
                  appSettings: appSettings.data,
                },
              })

              yield put({
                type: 'apps/INACTIVE_USERS_LIST',
                payload: { currentPage: 1, data: appData.data, appSettings: appSettings.data },
              })

              if (!isAppModerator(appData.data)) {
                yield put({
                  type: 'apps/EXTENSIONS_LIST',
                  data: appData.data,
                })
              }

              yield put({
                type: 'apps/ROLES_LIST',
                payload: { currentPage: 1, data: appData.data, appSettings: appSettings.data },
              })

              yield put({
                type: 'apps/GROUPS_LIST',
                payload: { currentPage: 1, data: appData.data, appSettings: appSettings.data },
              })
              yield put({
                type: 'apps/GROUPS_LIST_FOR_MODERATION',
                payload: { currentPage: 1, data: appData.data, appSettings: appSettings.data },
              })

              if (!isAppModerator(appData.data) && appData.data.region !== 'legacy') {
                // Bots are fetched by the Agents page (redux/customBots); fetch-all doesn't load them.
                yield put({
                  type: 'apps/WEBHOOKS_LIST',
                  payload: {
                    currentPage: 1,
                    data: appData.data,
                    appSettings: appSettings.data,
                  },
                })
                yield put({
                  type: 'apps/TRIGGERS_LIST',
                  payload: {
                    currentPage: 1,
                    data: appData.data,
                    appSettings: appSettings.data,
                  },
                })
                yield put({
                  type: 'apps/WEBHOOKS_LIST_NEW',
                  payload: {
                    currentPage: 1,
                    data: appData.data,
                    appSettings: appSettings.data,
                  },
                })
                yield put({
                  type: 'apps/TRIGGERS_LIST_NEW',
                  payload: {
                    currentPage: 1,
                    data: appData.data,
                    appSettings: appSettings.data,
                  },
                })
                yield put({
                  type: 'apps/ROLES_PERMISSIONS_LIST',
                  payload: { data: appData.data, appSettings: appSettings.data },
                })
              }
              if (!isAppDeveloper(appData.data) && appData.data.region !== 'legacy') {
                yield put({
                  type: 'apps/MODERATION_LIST_DROPPED_MESSAGES',
                  payload: {
                    currentPage: 1,
                    data: appData.data,
                    appSettings: appSettings.data,
                  },
                })
                yield put({
                  type: 'apps/MODERATION_LIST_RULES',
                  payload: {
                    currentPage: 1,
                    data: appData.data,
                    appSettings: appSettings.data,
                  },
                })

                yield put({
                  type: 'apps/MODERATION_LIST_KEYWORDS',
                  payload: {
                    currentPage: 1,
                    data: appData.data,
                    appSettings: appSettings.data,
                  },
                })
                yield put({
                  type: 'apps/MODERATION_LIST_CONFIGURATION',
                  payload: {
                    data: appData.data,
                    appSettings: appSettings.data,
                  },
                })
              }
            }

            if (otherAction !== '' && keyID !== '') {
              yield put({
                type: actions.SET_STATE,
                payload: {
                  active: appData.data,
                },
              })
              if (otherAction === 'GET_MOD_MESSAGE_LOGS' || otherAction === 'CHAT_WIDGETS_LIST') {
                yield put({
                  type: 'apps/EXTENSIONS_LIST',
                  data: appData.data,
                })
              }

              if (otherAction === 'GET_GROUP_MESSAGE_LOGS') {
                yield put({
                  type: 'apps/GET_GROUP_FOR_MODERATION',
                  payload: {
                    id: keyID,
                  },
                })
              }

              if (otherAction === 'GET_BILLING_HISTORY' && canListInvoices(appData.data)) {
                yield put({
                  type: 'billingHistory/GET_BILLING_HISTORY',
                  payload: {
                    active: appData.data,
                  },
                })
              }
              allCallsProcessed = yield putWait({
                type: `apps/${otherAction}`,
                payload: {
                  id: keyID,
                  data: appData.data,
                  appSettings: appSettings.data,
                  currentPage: 1,
                  id2: keyID2,
                },
              })
            } else {
              yield put({
                type: actions.SET_STATE,
                payload: {},
              })
            }
          } else {
            if (callSource === 'appCard' && isAppModerator(appData.data)) {
              yield put({
                type: 'apps/USERS_LIST',
                payload: {
                  currentPage: 1,
                  data: appData.data,
                  appSettings: appSettings.data,
                },
              })
            } else {
              yield put({
                type: 'apps/API_KEYS_LIST',
                payload: { currentPage: 1, data: appData.data, appSettings: appSettings.data },
              })
            }
            yield put({
              type: 'apps/TEAM_COLLABORATOR_LIST',
              payload: { currentPage: 1, data: appData.data },
            })
            yield put({
              type: actions.GET_APP_SETTINGS,
              data: appData.data,
            })
            yield put({
              type: actions.GET_APP_MESSAGE_SETTINGS,
              data: appData.data,
            })
            yield put({
              type: actions.SET_STATE,
              payload: {
                active: appData.data,
                appLoadingInProgress: false,
              },
            })
            if (callSource === 'appCard' && callSourceRedirectURL !== '') {
              yield put({
                type: 'apps/GET_USAGE_AND_OVERAGES',
                payload: { data: appData.data, appSettings: appSettings.data },
                redirectToIntegrate: true,
                callSourceRedirectURL,
              })
            }

            if (
              callSource === 'appCard' &&
              appData.data.plan !== undefined &&
              appData.data.plan === 'free' &&
              isAppOwner(appData.data)
            ) {
              yield put({
                type: 'settings/SET_STATE',
                payload: {
                  visibleModalID: 'appExpired',
                },
              })
            }
          }
        } else {
          yield put({
            type: actions.SET_STATE,
            payload: {
              active: appData.data,
              appLoadingInProgress: false,
            },
          })
          if (callSource === 'appCard') {
            yield put(push(`/app/${appData.data.id}/settings`))
          }
        }
      } else {
        if (
          appData.error !== undefined &&
          appData.error.code !== undefined &&
          isAuthErrorCode(appData.error.code)
        ) {
          if (appData.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
            yield put({
              type: 'user/LOGOUT',
            })
          } else {
            yield put(push('/login'))
          }
        } else if (appData && appData.error) {
          localStorage.removeItem('cc_lastOpenedApp')
          if (!openLastVisitedApp) {
            window.triggerNotification(appData.errDescription, 'error')
            yield put({
              type: actions.SET_STATE,
              payload: {
                openLastVisitedApp: false,
                appLoadingInProgress: false,
              },
            })
          } else {
            yield put({
              type: actions.SET_STATE,
              payload: {
                appLoadingInProgress: false,
              },
            })
          }
        }
        yield put(push('/apps'))
      }
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          active: [],
          stats: [],
          teamCollaboratorSearchText: '',
          teamCollaboratorsList: [],
          teamCollaboratorsListTotal: 0,
          appLoadingInProgress: false,
        },
      })
    }
    return allCallsProcessed
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        appLoadingInProgress: false,
      },
    })
  }
}

export function* GET() {
  try {
    yield put({
      type: actions.GET,
      payload: {
        loading: true,
      },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* EDIT({ id, payload, createAppFlow }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })
    const { name, metadata, noRedirect, noRedirectSuccessMsg } = payload

    const filtered = { name, metadata }
    let activeApp = reduxStore.getState().apps.active
    if (activeApp.id === undefined) {
      activeApp = reduxStore.getState().apps.appCreated
    }

    if (isAppAdmin(activeApp) || isAppOwner(activeApp) || createAppFlow) {
      const success = yield call(update, id, filtered, activeApp)
      if (success && success.data) {
        const allApps = reduxStore.getState().apps.data
        const appIndex = _.findIndex(allApps, function fn(a) {
          return a.id === activeApp.id
        })
        const appData = yield call(get, id)
        if (appIndex >= 0) {
          allApps[appIndex] = appData.data
        }
        if (noRedirect) {
          yield put({
            type: actions.SET_STATE,
            payload: {
              loading: false,
              active: appData.data,
            },
          })
          window.triggerNotification(noRedirectSuccessMsg, 'success')
        } else {
          yield put({
            type: actions.SET_STATE,
            payload: {
              loading: false,
              active: appData.data,
              messageLogs: [],
              callLogs: [],
            },
          })
          window.triggerNotification('App settings updated successfully', 'success')
        }
      } else if (success && success.error) {
        window.triggerNotification(success.errDescription, 'error')
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
        if (
          success.error !== undefined &&
          success.error.code !== undefined &&
          isAuthErrorCode(success.error.code)
        ) {
          if (success.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
            yield put({
              type: 'user/LOGOUT',
            })
          } else {
            yield put(push('/login'))
          }
        } else if (success.error.code === 'ERR_BAD_REQUEST') {
          window.triggerNotification(success.error.details.name, 'error')
        } else {
          window.triggerNotification('Error Updating the App', 'error')
        }
      }
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
        },
      })
      window.triggerNotification('Access Denied - Not Authorized', 'error')
      if (isAppModerator(activeApp)) {
        yield put(push(`/app/${activeApp.id}/users`))
      } else {
        yield put(push(`/app/${activeApp.id}/overview`))
      }
    }
    return activeApp
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* DELETE({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })

    const activeApp = reduxStore.getState().apps.active
    const { id } = payload

    const success = yield call(del, id, activeApp)

    if (success && success.data) {
      yield put({
        type: actions.ENTER_APP_TEXT,
        payload: {
          searchText: '',
          clearText: true,
        },
      })
      yield put({
        type: actions.LIST,
        payload: { currentPage: 1 },
      })

      yield put(push('/apps'))
      window.triggerNotification('App scheduled to be deleted after 30 days', 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
        },
      })
      if (
        success.error !== undefined &&
        success.error.code !== undefined &&
        isAuthErrorCode(success.error.code)
      ) {
        if (success.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
          yield put({
            type: 'user/LOGOUT',
          })
        } else {
          yield put(push('/login'))
        }
      } else if (
        (success.error.code !== undefined && success.error.code === 'ERR_BAD_REQUEST') ||
        success.error.code === 'ERR_APP_ARCHIVED'
      ) {
        window.triggerNotification(success.error.details.name, 'error')
      } else {
        window.triggerNotification('Something went wrong', 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* GET_APP_SETTINGS(activeApp = []) {
  try {
    const success = yield call(settingsGet, activeApp.data.id, activeApp.data)
    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          appSettings: success.data,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          appSettings: [],
        },
      })
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        appSettings: [],
      },
    })
  }
}

export function* GET_APP_MESSAGE_SETTINGS(activeApp = []) {
  try {
    const success = yield call(settingsThreadAndConversationGet, activeApp.data.id)
    if (success && success.data) {
      // appMessageSettings is owned by the messages slice (messages/reducers.js)
      yield put({
        type: messagesActions.SET_STATE,
        payload: {
          appMessageSettings: success.data,
        },
      })
    } else {
      yield put({
        type: messagesActions.SET_STATE,
        payload: {
          appMessageSettings: [],
        },
      })
    }
  } catch (error) {
    yield put({
      type: messagesActions.SET_STATE,
      payload: {
        appMessageSettings: [],
      },
    })
  }
}

export function* SAVE_APP_SETTINGS({ payload }) {
  try {
    const { id, data } = payload

    const activeApp = reduxStore.getState().apps.active
    const { appSettings } = reduxStore.getState().apps

    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })

    const success = yield call(settingsUpdate, id, data, activeApp, getAppHost(appSettings))

    if (success && success.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
        },
      })

      let successMessageParam
      if (data.settings.contactList === 'friends') {
        successMessageParam = ' enabled'
      } else {
        successMessageParam = ' disabled'
      }
      window.triggerNotification(
        `Friends' setting ${successMessageParam} successfully`,
        'success',
      )
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
        },
      })

      if (success.error.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(success.error.details.name, 'error')
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* SAVE_APP_MESSAGE_SETTINGS({
  payload,
  id,
  successMsg = 'Configurations updated successfully',
}) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })

    const success = yield call(settingsThreadAndConversationMap, id, payload)
    if (success && success.data) {
      const appData = yield call(settingsThreadAndConversationGet, id)
      if (appData && appData.data) {
        // appMessageSettings is owned by the messages slice (messages/reducers.js)
        yield put({
          type: messagesActions.SET_STATE,
          payload: {
            appMessageSettings: appData.data,
          },
        })
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
      }
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
        },
      })
      window.triggerNotification(successMsg, 'success')
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
        },
      })

      if (success.error.code === 'ERR_BAD_REQUEST') {
        window.triggerNotification(success.error.details.name, 'error')
      } else if (success.error.code && success.error.message) {
        window.triggerNotification(success.error.message, 'error')
      } else {
        window.triggerNotification(
          'Error updating configurations. Please contact us if error persists',
          'error',
        )
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* APPS_LIST() {
  try {
    yield put({
      type: actions.LIST,
      payload: { currentPage: 1 },
    })
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* UPDATE_APP_STATE({ payload }) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })
    const { id, state } = payload

    const activeApp = reduxStore.getState().apps.active
    if (isAppAdmin(activeApp) || isAppOwner(activeApp)) {
      const post = { state }
      const success = yield call(update, id, post, activeApp)
      if (success && success.data) {
        const allApps = reduxStore.getState().apps.data
        const appIndex = _.findIndex(allApps, function fn(a) {
          return a.id === activeApp.id
        })
        if (appIndex >= 0) {
          allApps[appIndex] = success.data
        }

        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
            active: success.data,
          },
        })
        yield put(push(`/app/${activeApp.id}/overview`))
        if (success.data.state === 'migrating') {
          window.triggerNotification(
            'App status set as Migrating successfullyy',
            'success',
          )
        } else {
          window.triggerNotification('App migrated to v3 successfully', 'success')
        }
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
        if (
          success.error !== undefined &&
          success.error.code !== undefined &&
          isAuthErrorCode(success.error.code)
        ) {
          if (success.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
            yield put({
              type: 'user/LOGOUT',
            })
          } else {
            yield put(push('/login'))
          }
        } else if (success.error.code === 'ERR_BAD_REQUEST') {
          window.triggerNotification(success.error.details.name, 'error')
        } else {
          window.triggerNotification('Error updating App', 'error')
        }
      }
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
        },
      })
      window.triggerNotification(
        'Access Denied - you are not authorized to perform this action',
        'error',
      )

      if (isAppModerator(activeApp)) {
        yield put(push(`/app/${activeApp.id}/users`))
      } else {
        yield put(push(`/app/${activeApp.id}/overview`))
      }
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* APP_MIGRATION_REQUEST_MANUAL_INTENT() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })
    const activeApp = reduxStore.getState().apps.active
    if (!_.isEmpty(activeApp) && activeApp.id !== undefined) {
      const intent = _.join(
        [
          constants.ManualOutreachIntents['app-migration-request-to-v3'],
          activeApp.id,
          activeApp.region,
        ],
        ' / ',
      )
      const post = { intent }

      const success = yield call(manualOutreach, post)

      if (success && success.data) {
        yield put({
          type: 'settings/SET_STATE',
          payload: {
            visibleModalID: 'intentRequestSuccess',
          },
        })
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
        if (
          success.error !== undefined &&
          success.error.code !== undefined &&
          isAuthErrorCode(success.error.code)
        ) {
          if (success.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
            yield put({
              type: 'user/LOGOUT',
            })
          } else {
            yield put(push('/login'))
          }
        } else if (success.error.code === 'ERR_BAD_REQUEST') {
          window.triggerNotification(success.error.details.name, 'error')
        }
      }

      return success
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* RESTORE_DELETED_APP_REQUEST() {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })
    const activeApp = reduxStore.getState().apps.active
    if (!_.isEmpty(activeApp) && activeApp.id !== undefined) {
      const intent = _.join(
        [
          constants.ManualOutreachIntents['restore-app-scheduled-for-deletion-request'],
          activeApp.id,
          activeApp.region,
        ],
        ' / ',
      )
      const post = { intent }

      const success = yield call(manualOutreach, post)
      if (success && success.data) {
        yield put({
          type: 'settings/SET_STATE',
          payload: {
            visibleModalID: 'intentSuccess',
          },
        })
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
        if (
          success.error !== undefined &&
          success.error.code !== undefined &&
          isAuthErrorCode(success.error.code)
        ) {
          if (success.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
            yield put({
              type: 'user/LOGOUT',
            })
          } else {
            yield put(push('/login'))
          }
        } else if (success.error.code === 'ERR_BAD_REQUEST') {
          window.triggerNotification(success.error.details.name, 'error')
        }
      }
      return success
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* UPDATE_APP_SETTINGS({
  id,
  payload,
  successModalId,
  notification = { hidden: false, msg: '' },
  suppressModal = false,
}) {
  try {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: true,
      },
    })
    let activeApp = reduxStore.getState().apps.active
    if (activeApp.id === undefined) {
      activeApp = reduxStore.getState().apps.appCreated
    }

    if (isAppAdmin(activeApp) || isAppOwner(activeApp)) {
      const success = yield call(updateSettings, id, payload, activeApp)
      if (success && success.data) {
        const allApps = reduxStore.getState().apps.data
        const appIndex = _.findIndex(allApps, function fn(a) {
          return a.id === activeApp.id
        })
        const appData = yield call(get, id)
        if (appIndex >= 0) {
          allApps[appIndex] = appData.data
        }

        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
            active: appData.data,
          },
        })
        if (!notification.hidden) {
          window.triggerNotification(notification.msg || 'App updated successfully', 'success')
        }

        if (successModalId) {
          yield put({
            type: 'settings/CHANGE_SETTING',
            payload: {
              setting: 'visibleModalID',
              value: successModalId,
            },
          })
        }
        if (payload[constants.featureSlugList.advanced_search_enabled] && !suppressModal) {
          yield put({
            type: 'settings/CHANGE_SETTING',
            payload: {
              setting: 'visibleModalID',
              value: 'ConversationSearchEnabled',
            },
          })
        }
      } else if (success && success.error) {
        window.triggerNotification(success.errDescription, 'error')
      } else {
        yield put({
          type: actions.SET_STATE,
          payload: {
            loading: false,
          },
        })
        if (
          success.error !== undefined &&
          success.error.code !== undefined &&
          isAuthErrorCode(success.error.code)
        ) {
          if (success.error.code === 'AUTH_ERR_TOKEN_BLACKLISTED') {
            yield put({
              type: 'user/LOGOUT',
            })
          } else {
            yield put(push('/login'))
          }
        } else if (success.error.code === 'ERR_BAD_REQUEST') {
          window.triggerNotification(success.error.details.name, 'error')
        } else {
          window.triggerNotification('Error Updating the App', 'error')
        }
      }
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          loading: false,
        },
      })
      window.triggerNotification('Access Denied - Not Authorized', 'error')
      if (isAppModerator(activeApp)) {
        yield put(push(`/app/${activeApp.id}/users`))
      } else {
        yield put(push(`/app/${activeApp.id}/overview`))
      }
    }
    return activeApp
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export function* GTM_TRIGGERED_MANUAL_INTENT({ payload }) {
  try {
    const activeApp = reduxStore.getState().apps.active
    if (!_.isEmpty(activeApp) && activeApp.id !== undefined) {
      const intent = _.join([payload.intent, activeApp.id], ' / ')
      const post = { intent }

      const success = yield call(manualOutreach, post)

      return success
    }
  } catch (error) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        loading: false,
      },
    })
  }
}

export default function* appCoreSaga() {
  yield all([
    takeEvery(actions.LIST, LIST),
    takeEvery(actions.ENTER_APP_TEXT, ENTER_APP_TEXT),
    takeLatest(actions.SEARCH_APP, SEARCH_APP),
    takeEvery(actions.REMOVE_ACTIVE_APP, REMOVE_ACTIVE_APP),
    takeEvery(actions.ADD, ADD),
    takeEvery(actions.ADD_APP_ON_ONBOARDING, ADD_APP_ON_ONBOARDING),
    takeEvery(actions.UPDATE_ACTIVE_APP, UPDATE_ACTIVE_APP),
    takeEvery(actions.GET, GET),
    takeEvery(actions.EDIT, EDIT),
    takeEvery(actions.DELETE, DELETE),
    takeEvery(actions.GET_APP_SETTINGS, GET_APP_SETTINGS),
    takeEvery(actions.GET_APP_MESSAGE_SETTINGS, GET_APP_MESSAGE_SETTINGS),
    takeEvery(actions.SAVE_APP_SETTINGS, SAVE_APP_SETTINGS),
    takeEvery(actions.SAVE_APP_MESSAGE_SETTINGS, SAVE_APP_MESSAGE_SETTINGS),
    takeEvery(actions.APPS_LIST, APPS_LIST),
    takeEvery(actions.UPDATE_APP_STATE, UPDATE_APP_STATE),
    takeEvery(actions.APP_MIGRATION_REQUEST_MANUAL_INTENT, APP_MIGRATION_REQUEST_MANUAL_INTENT),
    takeEvery(actions.RESTORE_DELETED_APP_REQUEST, RESTORE_DELETED_APP_REQUEST),
    takeEvery(actions.UPDATE_APP_SETTINGS, UPDATE_APP_SETTINGS),
    takeEvery(actions.GTM_TRIGGERED_MANUAL_INTENT, GTM_TRIGGERED_MANUAL_INTENT),
  ])
}
